'use client';

import { cn, Div, Span } from '@scatterlab/zeta-core';
import {
  ComponentPropsWithoutRef,
  createElement,
  ElementType,
  memo,
} from 'react';

import Skeleton from '../skeleton/Skeleton';

type LoadedHashtagsProps<HashtagComponent extends ElementType = typeof Span> = {
  hashtags?: string[];
  hashtagComponent?: HashtagComponent;
  hashtagProps?: (
    hashtag: string,
  ) => ComponentPropsWithoutRef<HashtagComponent>;
  className?: string;
};

export type HashtagsProps<HashtagComponent extends ElementType = typeof Span> =
  | (LoadedHashtagsProps<HashtagComponent> & {
      isLoading?: false;
    })
  | (Partial<LoadedHashtagsProps<HashtagComponent>> & {
      isLoading: true;
    });

const Hashtags = <HashtagComponent extends ElementType = typeof Span>({
  hashtags,
  hashtagComponent,
  hashtagProps,
  className,
  isLoading,
}: HashtagsProps<HashtagComponent>) => {
  if (isLoading) {
    return (
      <Div className={cn('flex flex-row gap-1 overflow-hidden', className)}>
        <Skeleton width={80} height={12} />
      </Div>
    );
  }
  if (!hashtags?.length) return null;
  return (
    <Div className={cn('flex flex-row gap-1 overflow-hidden', className)}>
      {hashtags?.length === 0 && hashtagProps?.('')?.placeholder ? (
        <Span className="body12 font-medium text-gray-700">
          {hashtagProps?.('').placeholder}
        </Span>
      ) : (
        hashtags.map((hashtag) => {
          const props = hashtagProps?.(hashtag);
          return createElement(
            hashtagComponent ?? Span,
            {
              key: hashtag,
              className: cn(
                'native:line-clamp-1 body12 whitespace-nowrap font-medium text-white/70',
                props?.className ?? '',
              ),
              ...props,
            },
            `#${hashtag}`,
          );
        })
      )}
    </Div>
  );
};

export default memo(Hashtags);
